import React, { useState, useRef } from 'react';
import { X, Download, Upload, FileSpreadsheet, CheckCircle, AlertTriangle } from 'lucide-react';
import { productsApi } from '../api';
import type { BulkUploadResult } from '../types';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkUploadModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<BulkUploadResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await productsApi.bulkTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data as any]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'amanah_tovarlar_namuna.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setErrorMsg('Namunani yuklab bo\'lmadi');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
    setErrorMsg('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    setErrorMsg('');
    try {
      const res = await productsApi.bulkUpload(file);
      setResult(res.data);
      if (res.data.success) {
        onSuccess();
      }
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.errors) {
        setResult(data);
      } else {
        setErrorMsg(data?.error || 'Yuklashda xatolik yuz berdi');
      }
    } finally {
      setUploading(false);
    }
  };

  const resetForUpload = () => {
    setFile(null);
    setResult(null);
    setErrorMsg('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-success-600" />
            Excel'dan ommaviy yuklash
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* 1-qadam: Namuna */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Namunani yuklab oling</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Excel namunasini yuklab olib, tovarlaringizni to'ldiring. Har bir tovar alohida qatorda.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                  className="btn btn-secondary flex items-center gap-2 mt-3 disabled:opacity-50"
                >
                  <Download size={18} />
                  {downloading ? 'Yuklanmoqda...' : 'Namunani yuklab olish'}
                </button>
              </div>
            </div>
          </div>

          {/* 2-qadam: Yuklash */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-medium">To'ldirilgan faylni yuklang</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Faqat <code>.xlsx</code> format. Maksimal 5MB, 1000 tagacha tovar.
                </p>

                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-600
                      file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0
                      file:text-sm file:font-medium file:bg-primary-50 file:text-primary-800
                      hover:file:bg-primary-100 cursor-pointer"
                  />
                  <button
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    className="btn btn-primary flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                  >
                    <Upload size={18} />
                    {uploading ? 'Yuklanmoqda...' : 'Yuklash'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Umumiy xato (fayl formati va h.k.) */}
          {errorMsg && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 text-sm flex items-center gap-2">
              <AlertTriangle size={18} />
              {errorMsg}
            </div>
          )}

          {/* Natija: muvaffaqiyat */}
          {result?.success && (
            <div className="rounded-lg bg-success-50 border border-success-200 p-4">
              <div className="flex items-center gap-2 text-success-700 font-medium">
                <CheckCircle size={20} />
                {result.created} ta tovar muvaffaqiyatli qo'shildi!
              </div>
            </div>
          )}

          {/* Natija: xatolar */}
          {result && !result.success && result.errors.length > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-3">
                <AlertTriangle size={20} />
                {result.errors.length} ta xatolik topildi — hech qanday tovar qo'shilmadi
              </div>
              <div className="border rounded-lg overflow-hidden bg-white max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 w-16">Qator</th>
                      <th className="text-left py-2 px-3">Maydon</th>
                      <th className="text-left py-2 px-3">Xato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="py-2 px-3 font-mono">{err.qator || '-'}</td>
                        <td className="py-2 px-3">
                          <div className="font-medium">{err.maydon || '-'}</div>
                          {err.qiymat && (
                            <div className="text-gray-400 text-xs truncate max-w-[120px]">"{err.qiymat}"</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-red-600">{err.xato}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={resetForUpload} className="btn btn-secondary mt-3">
                Tuzatib qayta yuklash
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="btn btn-secondary">
            {result?.success ? 'Yopish' : 'Bekor qilish'}
          </button>
        </div>
      </div>
    </div>
  );
};
